import React, { useState, useEffect } from 'react';
import { Card, Dropdown, Row, Col, ButtonGroup, Button } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom'; // Importa useNavigate
import Flex from 'components/common/Flex';
import Avatar from 'components/common/Avatar';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import CardDropdown from 'components/common/CardDropdown';
import PageHeader from 'components/common/PageHeader';
import { getAll } from 'services/users'; // Importa la función getAll

const columns = handleEdit => [
  {
    accessorKey: 'none',
    header: 'opciones',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' }
    },
    cell: ({ row: { original } }) => {
      return (
        <CardDropdown drop={'end'}>
          <div className="py-2">
            <Dropdown.Item onClick={() => handleEdit(original.id)}>
              Editar
            </Dropdown.Item>
          </div>
        </CardDropdown>
      );
    }
  },
  {
    accessorKey: 'user_name',
    header: 'Nombre',
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' }
    },
    cell: ({ row: { original } }) => {
      const { user_name, avatar } = original;
      return (
        <Link to="#!">
          <Flex alignItems="center" className="justify-content-center">
            {avatar ? (
              <Avatar src={avatar} size="xl" className="me-2" />
            ) : (
              <Avatar size="xl" name={user_name} className="me-2" />
            )}
            <div className="flex-1">
              <h5 className="mb-0 fs-10">{user_name}</h5>
            </div>
          </Flex>
        </Link>
      );
    }
  },
  {
    accessorKey: 'email',
    header: 'Correo Electrónico',
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' }
    },
    cell: ({ row: { original } }) => {
      const { email } = original;
      return <a href={`mailto:${email}`}>{email}</a>;
    }
  },
  {
    accessorKey: 'role',
    header: 'Rol',
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' }
    },
    cell: ({ row: { original } }) => {
      const { role } = original;
      return <span>{role}</span>;
    }
  },
  {
    accessorKey: 'is_active_human',
    header: 'Estado',
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' }
    }
  },
  {
    accessorKey: 'created_at',
    header: 'Fecha de Registro',
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' }
    }
  }
];

const UsersList = () => {
  const [users, setUsers] = useState([]); // Estado para almacenar los usuarios
  const [loading, setLoading] = useState(true); // Estado para manejar la carga
  const [filter, setFilter] = useState(1); // Estado para manejar el filtro (1: activos, 0: inactivos)
  const navigate = useNavigate(); // Hook para redirigir

  useEffect(() => {
    // Llama a la API para obtener los usuarios según el filtro
    setLoading(true); // Inicia la carga
    getAll(filter)
      .then(response => {
        setUsers(response); // Actualiza el estado con los datos obtenidos
        setLoading(false); // Finaliza la carga
      })
      .catch(error => {
        console.error('Error al obtener los usuarios:', error);
        setLoading(false); // Finaliza la carga incluso si hay un error
      });
  }, [filter]); // Ejecuta el efecto cuando cambia el filtro

  const handleEdit = id => {
    localStorage.setItem('selectedUserId', id); // Guarda el id en localStorage
    navigate(`/users/edit`);
  };

  const table = useAdvanceTable({
    data: users,
    columns: columns(handleEdit), // Pasa handleEdit a las columnas
    sortable: true, // Habilita el ordenamiento
    pagination: true, // Habilita la paginación
    perPage: 5 // Número de filas por página
  });

  return (
    <>
      <PageHeader
        title="Usuarios"
        description="Gestión de usuarios en el sistema"
        className="mb-3"
      />

      <AdvanceTableProvider {...table}>
        <Card className="mb-3">
          <Card.Header>
            <Row className="g-3 align-items-center">
              <Col xs={12} md={6} lg={4}>
                <h5 className="mb-0 text-center text-md-start">
                  Gestión de Usuarios
                </h5>
              </Col>
              <Col xs={12} md="auto" className="text-center">
                {/* Botones para cambiar el filtro */}
                <ButtonGroup size="sm">
                  {' '}
                  {/* Tamaño pequeño */}
                  <Button
                    variant={filter === 1 ? 'primary' : 'outline-primary'}
                    onClick={() => setFilter(1)}
                  >
                    Activos
                  </Button>
                  <Button
                    variant={filter === 0 ? 'primary' : 'outline-primary'}
                    onClick={() => setFilter(0)}
                  >
                    Inactivos
                  </Button>
                </ButtonGroup>
              </Col>
              <Col xs={12} md="auto" className="text-center">
                {/* Botón para agregar usuario */}
                <Button
                  variant="success"
                  size="sm" // Tamaño pequeño
                  onClick={
                    () => navigate('/users/add') // Redirige a la página de agregar usuario
                  }
                >
                  Agregar Usuario
                </Button>
              </Col>
              <Col xs={12} md={6} lg={4} className="text-center text-md-end">
                <AdvanceTableSearchBox />
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-3">Cargando usuarios...</div>
            ) : (
              <AdvanceTable
                headerClassName="bg-200 text-nowrap align-middle"
                rowClassName="align-middle white-space-nowrap"
                tableProps={{
                  size: 'sm',
                  striped: true,
                  bordered: true, // Bootstrap: agrega bordes a todas las celdas
                  className:
                    'fs-10 mb-0 overflow-hidden table-bordered table-hover', // table-bordered y table-hover para mejor diseño
                  style: {
                    borderCollapse: 'separate',
                    borderSpacing: 0
                  }
                }}
              />
            )}
          </Card.Body>
          <Card.Footer>
            <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
          </Card.Footer>
        </Card>
      </AdvanceTableProvider>
    </>
  );
};

export default UsersList;
